(ns leg.swipe
  "A finger dragged sideways, and which way it went.

  Written here rather than reached for, for the same reason the XML parser
  is: the window is one compiled file and a gesture library would be a
  dependency for forty lines of arithmetic.

  What counts as a swipe is the whole of it. Far enough to be deliberate,
  and much more sideways than up - a reader is a column that is scrolled,
  so almost every touch on it is vertical, and a threshold that let those
  through would turn scrolling into page-turning. One finger only: two are
  a pinch, and a page that jumped while somebody was zooming into a picture
  would be the worst kind of clever.

  It also has to keep its hands off what already scrolls sideways. An
  article can hold a code block or a wide table that is narrower than its
  contents, and dragging one of those along is the only way to read the end
  of the line - a drag that turned the page instead would make that text
  unreadable on a phone. So a gesture that starts inside one is not a
  gesture at all (`on-scroller?`), whichever way it then goes and whether or
  not there is any scrolling left in that direction: what a finger is for is
  decided where it goes down.

  The gesture in progress is kept in one atom for the whole window rather
  than in the component, because a component can be re-rendered between the
  touch going down and coming up - the server answers, the row greys out -
  and a half-finished swipe must not be lost with the closure that started
  it. There is one finger, so there is one gesture; a second `touchstart`
  replaces the first.

  The page follows the finger while it goes (`:track`), and that is why the
  first few pixels decide an axis and the rest of the gesture is held to it.
  A column is scrolled far more often than it is paged, so a touch that went
  down is a scroll for good and never moves the page sideways however it
  drifts afterwards; a touch that went sideways is a page-turn and is
  reported from then on, pixel by pixel. Ten pixels is early enough that
  nothing has visibly happened yet and late enough that a thumb coming off a
  phone cannot pick the wrong one.

  What it does not do is take the scroll away. A sideways drag could be
  stopped from scrolling the column at all - `touch-action: pan-y` on the
  box, or `preventDefault` on a listener that is not passive - and both would
  cost more than they are worth: the first disables the sideways scroll of
  every code block and wide table inside the article, which is the thing
  `on-scroller?` exists to protect, and the second is not available to a
  React handler. So a diagonal drag moves the page sideways and the column a
  few pixels up, which nobody has ever noticed, and the gesture keeps its
  hands off the browser."
  (:require [clojure.string :as str]))

(def min-distance
  "How far sideways, in pixels, before it was meant. Roughly a thumb's
  width: less than this is somebody holding the page still."
  60)

(def max-slope
  "How much more sideways than up. Below this it was a scroll that drifted."
  1.6)

(def lock-distance
  "How far a finger goes before the gesture is one thing or the other. Short
  enough that the page has not moved yet when it is decided, long enough
  that the decision is about a direction and not about jitter."
  10)

(defonce ^:private !start (atom nil))

(defn- single-touch
  "The one touch of a single-finger gesture, or nil for anything else."
  [^js touches]
  (when (and touches (= 1 (.-length touches)))
    (aget touches 0)))

(defn- scrolls-sideways?
  "Whether this element has a sideways scroll of its own with somewhere to
  go. A pixel of slack, because a fractional layout leaves `scrollWidth` a
  hair over `clientWidth` in things that do not scroll at all."
  [^js el]
  (and (contains? #{"auto" "scroll"} (.-overflowX (js/getComputedStyle el)))
       (> (.-scrollWidth el) (inc (.-clientWidth el)))))

(defn- on-scroller?
  "Whether the finger went down inside something that scrolls sideways -
  a code block, a wide table.

  Up to the swiped area and not past it: that box scrolls too, downwards,
  and asking about it would be asking whether the article is longer than
  the screen."
  [^js e]
  (let [root (.-currentTarget e)]
    (loop [^js el (.-target e)]
      (cond
        (or (nil? el) (identical? el root))     false
        (and (= 1 (.-nodeType el))
             (scrolls-sideways? el))            true
        :else                                   (recur (.-parentNode el))))))

(defn on-start [^js e]
  (reset! !start (when-let [t (and (not (on-scroller? e))
                                   (single-touch (.-touches e)))]
                   ;; `:axis` is what this gesture turned out to be, and it
                   ;; is not known yet: nil until the finger has gone far
                   ;; enough for it to be, `:sideways` or `:down` after that
                   ;; and never again anything else.
                   {:x (.-clientX t) :y (.-clientY t) :axis nil})))

(defn axis-of
  "Which of the two this gesture is, from where the finger has got to:
  `:sideways` a page-turn, `:down` a scroll, nil not far enough to say yet.
  Asked once per gesture and never again - see `on-move`."
  [dx dy]
  (cond
    (and (>= (abs dx) lock-distance)
         (> (abs dx) (* max-slope (abs dy)))) :sideways
    (>= (abs dy) lock-distance)               :down
    :else                                     nil))

(defn- direction
  "Which way this gesture went, or nil when it was not one."
  [^js e]
  (when-let [{:keys [x y axis]} @!start]
    ;; A gesture that locked downwards is a scroll and stays one, however
    ;; far sideways it wandered before it came up.
    (when-not (= :down axis)
      (when-let [t (single-touch (.-changedTouches e))]
        (let [dx (- (.-clientX t) x)
              dy (- (.-clientY t) y)]
          (when (and (>= (abs dx) min-distance)
                     (> (abs dx) (* max-slope (abs dy))))
            (if (pos? dx) :right :left)))))))

(defn- typing?
  "Whether the gesture started on something with its own idea of a
  sideways drag - a field being edited, or text being selected in one."
  [^js e]
  (when-let [tag (some-> e .-target .-tagName str/lower-case)]
    (contains? #{"input" "textarea" "select"} tag)))

(defn on-move
  "The `touchmove` handler. Once the gesture is a sideways one, `track` is
  called with how far sideways the finger is - from the point it went down,
  never from the last call, so a handler that missed a frame is still
  exactly where the finger is."
  [{:keys [track]}]
  (fn [^js e]
    (when-let [{:keys [x y axis]} @!start]
      (when-let [t (and (not (typing? e)) (single-touch (.-touches e)))]
        (let [dx   (- (.-clientX t) x)
              axis (or axis (axis-of dx (- (.-clientY t) y)))]
          (when axis (swap! !start assoc :axis axis))
          (when (and track (= :sideways axis)) (track dx)))))))

(defn on-end
  "The `touchend` handler for a swipeable area. `left` and `right` are which
  way the finger travelled; what moves which way is the caller's business
  (`leg.vt`). `settle` is called instead when the gesture came to nothing -
  the page was pushed aside and has to come back."
  [{:keys [left right settle]}]
  (fn [^js e]
    (let [d (when-not (typing? e) (direction e))]
      (reset! !start nil)
      (case d
        :left  (if left (left) (when settle (settle)))
        :right (if right (right) (when settle (settle)))
        (when settle (settle))))))

(defn on-cancel
  "The finger was taken away from the window rather than lifted off it - the
  phone rang, a second finger arrived. Nothing happens, and what was pushed
  aside comes back."
  [{:keys [settle]}]
  (fn [^js _e]
    (reset! !start nil)
    (when settle (settle))))
